/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

function collectCartSections() {
  const sections = new Map();
  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try { entries = el.getSectionsToRender?.(); } catch { continue; }
    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;
      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;
      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;
      sections.set(sectionId, { mount, extractSelector: entry.selector || '.shopify-section' });
    }
  }
  return sections;
}

async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size ? `?sections=${[...sections.keys()].join(',')}` : '';
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const cartData = await fetch(`${cartUrl}.js${sectionsQuery}`, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null)).catch(() => null);
  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }
  publish(PUB_SUB_EVENTS.cartUpdate, { source: 'external-refresh', cartData: cartData ?? undefined });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;
  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') { drawer.open(); return; }
      return defaultHandler();
    },
  });
  actions.updateCart.configure({
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try { await refreshDawnCartUI(); }
      catch (error) { console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error); window.location.reload(); }
      return result;
    },
  });
}

if (window.Shopify?.actions) initStandardActions();
else document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });

/* Sipee Beauty collection cleanup.
 * This is intentionally text-based so it works with the current Dawn
 * section markup even if Shopify regenerates section IDs/classes.
 */
(function beautyCollectionCleanup() {
  if (!/\/collections\/beauty(?:\/|$)/i.test(window.location.pathname)) return;

  const normalize = (value) => (value || '').replace(/\s+/g, ' ').trim().toLowerCase();

  function hideWhyBeauty() {
    const headings = document.querySelectorAll('h1,h2,h3,h4,h5,h6');
    for (const heading of headings) {
      const text = normalize(heading.textContent);
      if (text === 'why beauty' || text === 'why beauty?') {
        const section = heading.closest('.shopify-section, section') || heading.parentElement;
        if (section) section.style.setProperty('display', 'none', 'important');
      }
    }
  }

  function hideExploreBeauty() {
    const controls = document.querySelectorAll('a,button,[role="button"]');
    for (const control of controls) {
      const text = normalize(control.textContent);
      if (text === 'explore beauty' || text === 'explore beauty →' || text === 'explore beauty ›') {
        control.style.setProperty('display', 'none', 'important');
      }
    }
  }

  function apply() {
    hideWhyBeauty();
    hideExploreBeauty();
  }

  apply();
  document.addEventListener('DOMContentLoaded', apply, { once: true });
  new MutationObserver(apply).observe(document.documentElement, { childList: true, subtree: true });
})();
